import { useState, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Chip, Stack, IconButton, Tooltip,
  Dialog, DialogContent, DialogActions, Button, TextField, MenuItem, Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import type { KanbanCard, TaskState } from '@pm/shared';
import {
  KANBAN_STATES, normalizeKanbanState, type ProjectKanbanBoard,
  useCreateKanbanCard, useUpdateKanbanCard, useDeleteKanbanCard, useCreateKanbanBoard,
} from '../../../../api/hooks/useKanban';
import { PmRichTextEditor, isRichTextEmpty } from '../../../../components/pm/RichTextEditor';
import { useNotifications } from '../../../../context/NotificationContext';
import { KanbanCardItem } from './KanbanCardItem';
import { apiErrorMessage } from '../../../../lib/apiErrorMessage';
import { AppDialogTitle } from '../../../../components/common/AppDialogTitle';

const COLUMN_COLORS: Record<string, string> = {
  PLANNED: '#90a4ae', IN_PROGRESS: '#1D8296', COMPLETED: '#66bb6a',
};

interface CardDraft {
  id?: string;
  apId: string;
  title: string;
  description: string;
  person: string;
  state: TaskState;
}

/** Minimaler AP-Eintrag fürs Anlege-Dropdown (volle Projekt-AP-Liste). */
export interface ApLite { id: string; nr: string; titel: string | null; }

interface Props {
  projectId: string;
  /** Aktive AP-Boards des Projekts (flach, je Karte ap_id; Board trägt ap_nr/ap_titel). */
  boards: ProjectKanbanBoard[];
  /** Alle Arbeitspakete des Projekts — auch ohne aktives Board (Board wird beim
   *  ersten Kartenanlegen on-demand erzeugt). */
  allAps: ApLite[];
  showHidden: boolean;
  /** Personen-Picker-Optionen (P-14): RACI-Personen aller APs des Projekts. */
  personOptions?: string[];
}

/** Projektweiter Kanban-Modus (T-071): alle aktiven AP-Karten in EINEM Board,
 *  je Karte ein AP-Herkunfts-Chip. Karten liegen physisch weiter am AP-Board. */
export function ProjectKanbanView({ projectId, boards, allAps, showHidden, personOptions = [] }: Props) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();
  const createCard = useCreateKanbanCard(projectId);
  const updateCard = useUpdateKanbanCard(projectId);
  const deleteCard = useDeleteKanbanCard(projectId);
  const createBoard = useCreateKanbanBoard(projectId);

  const [dragOverCol, setDragOverCol] = useState<string | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<CardDraft | null>(null);
  const [saving, setSaving] = useState(false);

  // AP-Herkunft je Karte (volle AP-Liste) + vorhandenes Board-Ziel je AP.
  const apNrById = useMemo(() => new Map(allAps.map((a) => [a.id, a.nr])), [allAps]);
  const boardIdByAp = useMemo(() => new Map(boards.map((b) => [b.ap_id, b.id])), [boards]);
  // Anlegen ist für JEDES AP möglich; fehlt ein Board, wird es on-demand erzeugt.
  const apOptions = useMemo(
    () => [...allAps].sort((a, b) => a.nr.localeCompare(b.nr, undefined, { numeric: true, sensitivity: 'base' })),
    [allAps],
  );
  const allCards = useMemo(() => boards.flatMap((b) => b.cards), [boards]);

  const handleDrop = useCallback(async (e: React.DragEvent, targetState: TaskState) => {
    e.preventDefault();
    setDragOverCol(null);
    setDraggedId(null);
    const cardId = e.dataTransfer.getData('text/plain');
    if (!cardId) return;
    const card = allCards.find((c) => c.id === cardId);
    if (!card || card.state === targetState) return;
    try {
      await updateCard.mutateAsync({ apId: card.ap_id, cardId, data: { state: targetState } });
    } catch (err) {
      notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error');
    }
  }, [allCards, updateCard, notify, tc]);

  const toggleHidden = async (card: KanbanCard) => {
    try {
      await updateCard.mutateAsync({ apId: card.ap_id, cardId: card.id, data: { hidden: !card.hidden } });
    } catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
  };

  const removeCard = async (card: KanbanCard) => {
    try {
      await deleteCard.mutateAsync({ apId: card.ap_id, cardId: card.id });
    } catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
  };

  // P-17: Karte abschließen/wiedereröffnen — reiner State-Wechsel, kein Löschen/Umhängen.
  const completeCard = async (card: KanbanCard) => {
    try {
      await updateCard.mutateAsync({ apId: card.ap_id, cardId: card.id, data: { state: 'COMPLETED' } });
    } catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
  };

  const reopenCard = async (card: KanbanCard) => {
    try {
      await updateCard.mutateAsync({ apId: card.ap_id, cardId: card.id, data: { state: 'IN_PROGRESS' } });
    } catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
  };

  const saveDraft = async () => {
    if (!draft) return;
    if (!draft.apId) { notify(t('kanban.pickAp', 'Bitte ein Arbeitspaket wählen'), 'warning'); return; }
    const data = {
      title: draft.title.trim(),
      description: isRichTextEmpty(draft.description) ? null : draft.description,
      person: draft.person.trim() || null,
      state: draft.state,
    };
    setSaving(true);
    try {
      if (draft.id) {
        await updateCard.mutateAsync({ apId: draft.apId, cardId: draft.id, data });
      } else {
        // Board-on-demand: hat das Ziel-AP noch kein aktives Board, erst eins
        // anlegen (Backend seedet dabei die R-Karte), dann die Karte einhängen.
        let boardId = boardIdByAp.get(draft.apId);
        if (!boardId) {
          const res = await createBoard.mutateAsync(draft.apId);
          boardId = (res as { data?: { id?: string } } | undefined)?.data?.id;
        }
        if (!boardId) { notify(tc('errors.generic', 'Fehler'), 'error'); return; }
        await createCard.mutateAsync({ apId: draft.apId, boardId, data });
      }
      setDraft(null);
    } catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
    finally { setSaving(false); }
  };

  const grouped = KANBAN_STATES.reduce<Record<string, KanbanCard[]>>((acc, col) => {
    acc[col] = allCards.filter((c) => normalizeKanbanState(c.state) === col && (showHidden || !c.hidden));
    return acc;
  }, {});

  return (
    <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
      {KANBAN_STATES.map((col) => {
        const isDropTarget = dragOverCol === col;
        return (
          <Paper
            key={col}
            variant="outlined"
            sx={{
              minWidth: 240, maxWidth: 360, flex: 1, display: 'flex', flexDirection: 'column',
              bgcolor: 'background.default',
              ...(isDropTarget ? { boxShadow: `inset 0 0 0 2px ${COLUMN_COLORS[col]}`, bgcolor: 'action.hover' } : {}),
            }}
            onDragOver={(e) => { e.preventDefault(); setDragOverCol(col); }}
            onDragLeave={() => setDragOverCol(null)}
            onDrop={(e) => handleDrop(e, col)}
          >
            <Box sx={{ px: 1.5, py: 0.75, borderBottom: '3px solid', borderColor: COLUMN_COLORS[col] ?? 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" fontWeight={700}>{t(`states.${col}`, col)}</Typography>
                <Chip label={grouped[col].length} size="small" variant="outlined" />
              </Stack>
              <Tooltip title={t('kanban.addCard', 'Karte hinzufügen')}>
                <span>
                  <IconButton
                    size="small"
                    sx={{ color: COLUMN_COLORS[col] }}
                    disabled={apOptions.length === 0}
                    onClick={() => setDraft({ apId: apOptions[0]?.id ?? '', title: '', description: '', person: '', state: col })}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Box sx={{ p: 1, flex: 1, overflowY: 'auto', minHeight: 60 }}>
              <Stack spacing={1}>
                {grouped[col].map((card) => (
                  <KanbanCardItem
                    key={card.id}
                    card={card}
                    active
                    dragging={draggedId === card.id}
                    apChipLabel={apNrById.get(card.ap_id)}
                    onDragStart={(e) => { e.dataTransfer.setData('text/plain', card.id); setDraggedId(card.id); }}
                    onDragEnd={() => setDraggedId(null)}
                    onEdit={() => setDraft({ id: card.id, apId: card.ap_id, title: card.title, description: card.description ?? '', person: card.person ?? '', state: card.state })}
                    onToggleHidden={() => toggleHidden(card)}
                    onRemove={() => removeCard(card)}
                    onComplete={() => completeCard(card)}
                    onReopen={() => reopenCard(card)}
                  />
                ))}
                {grouped[col].length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 2, display: 'block' }}>
                    {t('kanban.emptyColumn', '—')}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Paper>
        );
      })}

      <Dialog open={!!draft} onClose={() => setDraft(null)} maxWidth="sm" fullWidth>
        <AppDialogTitle>{draft?.id ? t('kanban.editCard', 'Karte bearbeiten') : t('kanban.addCard', 'Karte hinzufügen')}</AppDialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select label={t('kanban.cardAp', 'Arbeitspaket')} value={draft?.apId ?? ''} fullWidth
              disabled={!!draft?.id}
              helperText={draft?.id ? t('kanban.cardApLocked', 'Herkunfts-Arbeitspaket') : undefined}
              onChange={(e) => setDraft((d) => d && { ...d, apId: e.target.value })}
            >
              {apOptions.map((ap) => (
                <MenuItem key={ap.id} value={ap.id}>{`${ap.nr}${ap.titel ? ` · ${ap.titel}` : ''}`}</MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('kanban.cardTitle', 'Titel')} value={draft?.title ?? ''} fullWidth autoFocus
              onChange={(e) => setDraft((d) => d && { ...d, title: e.target.value })}
            />
            <Box>
              <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)', mb: 0.5, display: 'block' }}>
                {t('kanban.cardDescription', 'Beschreibung')}
              </Typography>
              <PmRichTextEditor
                value={draft?.description ?? ''}
                onChange={(html) => setDraft((d) => d && { ...d, description: html })}
                placeholder={t('kanban.cardDescriptionPlaceholder', 'Beschreibung der Aufgabe …')}
                minHeight={120}
              />
            </Box>
            <Autocomplete
              freeSolo
              options={personOptions}
              value={draft?.person ?? ''}
              onChange={(_e, newValue) => setDraft((d) => d && { ...d, person: newValue ?? '' })}
              onInputChange={(_e, newInputValue) => setDraft((d) => d && { ...d, person: newInputValue })}
              renderInput={(params) => (
                <TextField {...params} label={t('kanban.cardPerson', 'Person')} fullWidth />
              )}
            />
            <TextField
              select label={t('kanban.cardState', 'Status')} value={draft?.state ?? 'PLANNED'} fullWidth
              onChange={(e) => setDraft((d) => d && { ...d, state: e.target.value as TaskState })}
            >
              {KANBAN_STATES.map((s) => (
                <MenuItem key={s} value={s}>{t(`states.${s}`, s)}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDraft(null)}>{tc('actions.cancel', 'Abbrechen')}</Button>
          <Button variant="contained" disabled={!draft?.title.trim() || !draft?.apId || saving} onClick={saveDraft}>{tc('actions.save', 'Speichern')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
